"use client"

import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { TestTube } from "lucide-react"
import { toast } from "sonner"
import { useTestMappings } from "@/hooks/use-test-mappings"
import { TestResultsSingle } from "@/components/testing/test-results-single"
import { SourceFieldTooltip } from "@/components/shared/source-field-tooltip"
import type { SingleTestResult } from "@/types/ui"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

interface SingleFieldTestProps {
  entityTypes: string[]
  atsSource: string
  // Form values
  entityType: string
  entityId: string
  sourceField: string
  // Form setters
  onEntityTypeChange: (value: string) => void
  onEntityIdChange: (value: string) => void
  onSourceFieldChange: (value: string) => void
}

export function SingleFieldTest({
  entityTypes,
  atsSource,
  entityType,
  entityId,
  sourceField,
  onEntityTypeChange,
  onEntityIdChange,
  onSourceFieldChange,
}: SingleFieldTestProps) {
  const { t } = useTranslation()

  // Component state
  const [testResults, setTestResults] = useState<SingleTestResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [validationMessage, setValidationMessage] = useState<string | null>(null)

  const { testSingleSourceField } = useTestMappings()

  const handleTest = async () => {
    setIsLoading(true)
    setTestResults(null)
    setValidationMessage(null)

    try {
      // Using the exact same logic from the original handleTestMappings function
      const result = await testSingleSourceField({
        entityType,
        entityId,
        sourceField,
        atsSource,
      })

      if (result.success && result.data) {
        const notify = result.data.success ? toast.success : toast.error
        notify(t("testing.toasts.completeTitle"), {
          description: t((result.data.success ? "testing.toasts.singleSuccess" : "testing.toasts.singleFailure")),
        })
        setTestResults(result.data)
      } else {
        setValidationMessage(result.validationMessage || t("testing.errors.testFailed"))
      }
    } catch (error) {
      toast.error(t("testing.toasts.failedTitle"), {
        description: t("testing.toasts.failed"),
      })
    } finally {
      setIsLoading(false)
    }
  }

  const isFormValid = entityType && entityId && sourceField

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t("testing.sourceField.title")}</CardTitle>
        <CardDescription>{t("testing.sourceField.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Form fields - exact copy from original */}
        <div className="space-y-2">
          <Label htmlFor="inlineTestEntityType">{t("testing.sourceField.entityType")}</Label>
          <Select value={entityType} onValueChange={onEntityTypeChange}>
            <SelectTrigger>
              <SelectValue placeholder={t("testing.sourceField.entityTypePlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {entityTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="inlineTestEntityId">{t("testing.sourceField.entityId")}</Label>
          <Input
            id="inlineTestEntityId"
            placeholder={t("testing.sourceField.entityIdPlaceholder")}
            value={entityId}
            onChange={(e) => onEntityIdChange(e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Label htmlFor="inlineTestSourceField">{t("testing.sourceField.sourceField")}</Label>
            <SourceFieldTooltip />
          </div>
          <Input
            id="inlineTestSourceField"
            placeholder={t("testing.sourceField.sourceFieldPlaceholder")}
            value={sourceField}
            onChange={(e) => onSourceFieldChange(e.target.value)}
          />
        </div>
        
        <Button
          onClick={handleTest}
          disabled={!isFormValid || isLoading}
          className="w-full"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t("testing.sourceField.testing")}
            </>
          ) : (
            <>
              <TestTube className="h-4 w-4 mr-2" />
              {t("testing.sourceField.run")}
            </>
          )}
        </Button>

        {/* Validation Error Display */}
        {validationMessage && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <div className="flex-shrink-0">
                <div className="w-5 h-5 rounded-full bg-destructive flex items-center justify-center">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-destructive">{t("testing.sourceField.errorTitle")}</h4>
                <p className="text-sm text-destructive/80 mt-1">{validationMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Inline Results Display */}
        {testResults && <TestResultsSingle results={testResults} />}
      </CardContent>
    </Card>
  )
}
